import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Form } from "react-final-form";
import classNames from "classnames";
import Input from "rs-components/Input";
import ToggleButtonInput from "rs-components/Input/components/ToggleButtonInput";
import TextInput from "rs-components/Input/components/TextInput";
import IconButton from "components/IconButton";
import HtmlText from "components/HtmlText";
import RadioInputGroup, { RadioInputViewType } from "components/Controls/RadioInputGroup";
import EntityDialog from "rs-components/EntityDialog";
import TwoValuesFormulaContent from "../TwoValuesFormulaContent";
import SingleValueFormulaContent from "../SingleValueFormulaContent";
import ZScoreFormulaContent from "../ZScoreFormulaContent";
import SaturationScoreFormulaContent from "../SaturationScoreFormulaContent";
import LogisticScoreFormulaContent from "../LogisticScoreFormulaContent";
import ReferencesLabel from "pages/Recipe/components/ReferencesLabel";
import { CustomVariableFormulaType, ParameterTypesCategoryType, RecipeDialogType, } from "store/recipe/recipe.types";
import { RsWorkspaceEntityType } from "shared/types";
import { GetRestrictionsMethod, RequestTemplate } from "store/knowledge-base-item/knowledge-base-item.types";
import { addRecipeCustomVariable, editRecipeCustomVariable, getRecipeCustomVariableDefaultName, getRecipeCustomVariableFormulaStatus, setRecipeCustomVariableFormulaStatus, setRecipeDialogStates, } from "store/recipe/recipe.actions";
import { currentCustomVariableSelector, customVariableCurrentFormulaStatusSelector, customVariableReferencesDataSelector, customVariableRelativeParameterOptionsSelector, customVariableRelativeVariableOptionsSelector, getRecipeDialogState, recipeCustomVariableDefaultNameSelector, } from "store/recipe/recipe.selectors";
import { isEditableSelector, restrictionsSelector, } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { loadRecipeList } from "store/recipe-list/recipe-list.actions";
import { getRestrictions } from "store/knowledge-base-item/knowledge-base-item.actions";
import { getFieldRestrictions, getRequestName } from "store/knowledge-base-item/knowledge-base-item.utils";
import { getCustomVariableDialogInitialValues, isTwoValuesFormula, isSingleValueFormula, getFormulaFieldsConfigured, getFormulaFieldsErrored, parseComplexUnitToHtml, getDetailedFormulaUnits, } from "store/recipe/custom-variables.utils";
import { customVariableFormulas } from "shared/rsDictionary";
import { RecipeScalingApiEndpoints } from "api/api-endpoints";
import { CUSTOM_VARIABLES_LABELS, CUSTOM_VARIABLE_REFERENCED_LABELS } from "shared/labels";
import WarningIcon from "icons/info_triangle.svg";
import InfoIcon from "icons/icn-info.svg";
import WarningSquareIcon from "icons/validationWarning.svg";
import InfoSquareIcon from "icons/validationInfo.svg";
import * as styles from "./styles.module.scss";
const getRestrictionsPayload = (post) => ({
    rsEndpoint: RecipeScalingApiEndpoints.recipes,
    template: post ? RequestTemplate.CUSTOM_VARIABLE_POST : RequestTemplate.CUSTOM_VARIABLE_PUT,
    method: post ? GetRestrictionsMethod.POST : GetRestrictionsMethod.PUT,
});
export var CustomVariableFormFields;
(function (CustomVariableFormFields) {
    CustomVariableFormFields["name"] = "name";
    CustomVariableFormFields["recordInData"] = "recordInData";
    CustomVariableFormFields["note"] = "note";
    CustomVariableFormFields["formula"] = "formula";
    CustomVariableFormFields["type"] = "type";
    CustomVariableFormFields["value"] = "value";
    CustomVariableFormFields["units"] = "units";
    CustomVariableFormFields["percentage"] = "percentage";
    // transformations
    CustomVariableFormFields["transformationType"] = "transformationType";
    CustomVariableFormFields["timestampType"] = "timestampType";
    CustomVariableFormFields["_timestampType"] = "_timestampType";
    CustomVariableFormFields["cut"] = "cut";
    CustomVariableFormFields["point"] = "point";
    CustomVariableFormFields["start"] = "start";
    CustomVariableFormFields["end"] = "end";
    CustomVariableFormFields["offset"] = "offset";
    CustomVariableFormFields["offsetUnit"] = "offsetUnit";
    // relative value sub-types
    CustomVariableFormFields["of"] = "of";
    CustomVariableFormFields["variableType"] = "variableType";
    CustomVariableFormFields["variableId"] = "variableId";
    CustomVariableFormFields["parameterId"] = "parameterId";
    CustomVariableFormFields["parameterType"] = "parameterType";
    CustomVariableFormFields["_variableName"] = "_variableName";
    CustomVariableFormFields["_variableUnit"] = "_variableUnit";
    CustomVariableFormFields["_relativeValueType"] = "_relativeValueType";
    // Two values fields
    CustomVariableFormFields["first"] = "first";
    CustomVariableFormFields["second"] = "second";
    // z-score fields
    CustomVariableFormFields["referenceValue"] = "referenceValue";
    CustomVariableFormFields["optimum"] = "optimum";
    CustomVariableFormFields["spread"] = "spread";
    // saturation-score fields
    CustomVariableFormFields["value50"] = "value50";
    CustomVariableFormFields["referenceLow"] = "referenceLow";
    CustomVariableFormFields["referenceHigh"] = "referenceHigh";
    // logistic-score fields
    CustomVariableFormFields["threshold"] = "threshold";
    CustomVariableFormFields["sensitivity"] = "sensitivity";
})(CustomVariableFormFields || (CustomVariableFormFields = {}));
const CustomVariableDialog = () => {
    const { id: recipeId } = useParams();
    const dispatch = useDispatch();
    const { id: variableId } = useSelector(getRecipeDialogState(RecipeDialogType.CUSTOM_VARIABLE));
    const editable = useSelector(isEditableSelector);
    const referencesData = useSelector(customVariableReferencesDataSelector(variableId));
    const customVariableDefaultName = useSelector(recipeCustomVariableDefaultNameSelector);
    const currentCustomVariable = useSelector(currentCustomVariableSelector);
    const currentFormulaStatus = useSelector(customVariableCurrentFormulaStatusSelector);
    const relativeVariableOptions = useSelector(customVariableRelativeVariableOptionsSelector);
    const relativeParameterOptions = useSelector(customVariableRelativeParameterOptionsSelector);
    const restrictions = useSelector(restrictionsSelector(getRestrictionsPayload(!variableId)));
    const initialValues = useMemo(() => getCustomVariableDialogInitialValues(currentCustomVariable, relativeVariableOptions, relativeParameterOptions, customVariableDefaultName), [currentCustomVariable, relativeVariableOptions, relativeParameterOptions, customVariableDefaultName]);
    const unitsError = useMemo(() => currentFormulaStatus?.configuredUnits?.error, [currentFormulaStatus]);
    const unitsWarningMessage = useMemo(() => {
        if (currentFormulaStatus?.configuredUnits?.category !== ParameterTypesCategoryType.Custom) {
            return "";
        }
        return CUSTOM_VARIABLES_LABELS.COMPLEX_UNIT_WARNING;
    }, [currentFormulaStatus?.configuredUnits?.category]);
    const variableUnits = useMemo(() => parseComplexUnitToHtml(currentFormulaStatus?.configuredUnits?.complexValue), [currentFormulaStatus?.configuredUnits?.complexValue]);
    const detailedFormulaUnits = useMemo(() => getDetailedFormulaUnits(currentFormulaStatus), [currentFormulaStatus]);
    const referencedWarning = useMemo(() => {
        const { customVariablesCount, recipeSteps, liquidsCount } = referencesData;
        let message = "";
        let firstSentence = CUSTOM_VARIABLE_REFERENCED_LABELS.BASE1;
        let secondSentence = CUSTOM_VARIABLE_REFERENCED_LABELS.BASE2;
        const affectedEntities = [];
        if (customVariablesCount > 0) {
            affectedEntities.push(" Custom Variables");
        }
        if (recipeSteps?.length > 0) {
            affectedEntities.push(" Recipe steps");
        }
        if (liquidsCount > 0) {
            affectedEntities.push(" Liquids");
        }
        firstSentence = `${firstSentence}${affectedEntities.join().split(",")}`;
        secondSentence = `${secondSentence}${affectedEntities.join().split(",")}`;
        message = `${firstSentence}. ${secondSentence}.`;
        if (customVariablesCount > 0 && recipeSteps?.length > 0 && liquidsCount > 0) {
            message = CUSTOM_VARIABLE_REFERENCED_LABELS.ALL_ENTITIES;
        }
        return message;
    }, [referencesData]);
    const getVariableUnits = useCallback(() => (React.createElement(React.Fragment, null,
        referencesData?.hasReferences && (React.createElement(ReferencesLabel, { nodeName: "Custom variable", customVariablesCount: referencesData?.customVariablesCount, steps: referencesData?.recipeSteps, liquidsCount: referencesData?.liquidsCount, className: styles.references })),
        React.createElement("div", { className: styles.variableUnits },
            !!unitsError && (React.createElement(React.Fragment, null,
                React.createElement(IconButton, { title: unitsError, classes: { root: styles.icon }, icon: React.createElement(WarningIcon, { className: styles.warning }) }),
                React.createElement(HtmlText, { targetClassName: classNames(styles.text, styles.red), text: detailedFormulaUnits }))),
            !!unitsWarningMessage && !unitsError && (React.createElement(IconButton, { title: CUSTOM_VARIABLES_LABELS.COMPLEX_UNIT_WARNING, classes: { root: styles.icon }, icon: React.createElement(InfoIcon, { className: styles.info }) })),
            !unitsError && (React.createElement(HtmlText, { targetClassName: classNames(styles.text, { [styles.grey]: !variableUnits?.html }), text: variableUnits?.html || "Not specified" }))))), [unitsError, variableUnits, referencesData, detailedFormulaUnits, unitsWarningMessage]);
    const getEntityStatuses = useCallback((values, errors) => {
        const formulaFieldsConfigured = getFormulaFieldsConfigured(values);
        const formulaFieldsErrored = getFormulaFieldsErrored(values, errors);
        const errored = formulaFieldsErrored ||
            (!!currentFormulaStatus &&
                formulaFieldsConfigured &&
                !currentFormulaStatus?.configuredUnits?.isSuccess);
        const completed = !errored && formulaFieldsConfigured;
        return {
            completed,
            errored,
        };
    }, [currentFormulaStatus]);
    const onDialogClose = useCallback(() => {
        dispatch(setRecipeDialogStates({ [RecipeDialogType.CUSTOM_VARIABLE]: { isOpen: false } }));
        dispatch(setRecipeCustomVariableFormulaStatus(null));
    }, [dispatch]);
    const onAddCustomVariable = useCallback((values, form) => {
        if (!form?.getState()?.dirty) {
            onDialogClose();
            return;
        }
        if (variableId) {
            dispatch(editRecipeCustomVariable({ recipeId: parseInt(recipeId), variableId, data: values }));
        }
        else {
            dispatch(addRecipeCustomVariable({ recipeId: parseInt(recipeId), data: values }));
        }
    }, [dispatch, recipeId, variableId, onDialogClose]);
    const onFormulaTypeChange = useCallback((form, newType) => {
        setTimeout(() => {
            form.change(`${CustomVariableFormFields.formula}`, { [CustomVariableFormFields.type]: newType });
        }, 0);
        dispatch(setRecipeCustomVariableFormulaStatus(null));
    }, [dispatch]);
    const renderContent = useCallback((formulaType) => {
        if (isTwoValuesFormula(formulaType)) {
            return React.createElement(TwoValuesFormulaContent, { restrictions: restrictions });
        }
        if (isSingleValueFormula(formulaType)) {
            return React.createElement(SingleValueFormulaContent, { restrictions: restrictions });
        }
        if (formulaType === CustomVariableFormulaType.ZScore) {
            return React.createElement(ZScoreFormulaContent, { restrictions: restrictions });
        }
        if (formulaType === CustomVariableFormulaType.SaturationScore) {
            return React.createElement(SaturationScoreFormulaContent, { restrictions: restrictions });
        }
        if (formulaType === CustomVariableFormulaType.LogisticScore) {
            return React.createElement(LogisticScoreFormulaContent, { restrictions: restrictions });
        }
        return null;
    }, [restrictions]);
    const onDialogOpening = useCallback(() => {
        if (!variableId) {
            dispatch(getRecipeCustomVariableDefaultName(parseInt(recipeId)));
        }
        if (!!currentCustomVariable && !currentFormulaStatus) {
            dispatch(getRecipeCustomVariableFormulaStatus({
                recipeId: parseInt(recipeId),
                formula: currentCustomVariable.formula,
            }));
        }
    }, [dispatch, recipeId, variableId, currentCustomVariable, currentFormulaStatus]);
    useEffect(() => {
        !restrictions && dispatch(getRestrictions(getRestrictionsPayload(!variableId)));
    }, [dispatch, variableId, restrictions]);
    useEffect(() => {
        dispatch(loadRecipeList());
    }, [dispatch]);
    return (React.createElement(Form, { onSubmit: onAddCustomVariable, initialValues: initialValues, validateOnBlur: true, render: ({ handleSubmit, form, values, ...renderProps }) => (React.createElement(EntityDialog, { nameFieldName: CustomVariableFormFields.name, nameFieldRestrictions: getFieldRestrictions(restrictions, [
                getRequestName(CustomVariableFormFields.name),
            ]), entityType: RsWorkspaceEntityType.CustomVariable, isOpen: true, onDialogClose: onDialogClose, headerSuffix: getVariableUnits(), onDialogOpening: onDialogOpening, onClosing: async () => await form.reset(), entityCompleted: getEntityStatuses(values, renderProps.errors)?.completed, entityErrored: getEntityStatuses(values, renderProps.errors)?.errored, handleSubmit: editable ? handleSubmit : onDialogClose, formRenderProps: renderProps },
            React.createElement("div", { className: styles.root },
                React.createElement("div", { className: styles.sidebar },
                    React.createElement("div", { className: styles.header },
                        React.createElement(Input, { component: ToggleButtonInput, form: form, autoFocus: true, name: CustomVariableFormFields.recordInData, alignIndicator: "right", disabled: !editable, classes: { component: styles.toggle } }, "Record in data")),
                    React.createElement("div", { className: styles.functions },
                        React.createElement("h2", null, "Function"),
                        React.createElement("div", { className: styles.list }, customVariableFormulas.map(({ value, label }) => (React.createElement(Input, { key: value, name: `${CustomVariableFormFields.formula}.${CustomVariableFormFields.type}`, component: RadioInputGroup, viewType: RadioInputViewType.RADIO, items: [
                                {
                                    value,
                                    label,
                                    className: classNames(styles.radio, { [styles.readonly]: !editable }),
                                },
                            ], classes: { container: styles.listItem }, disableFormChange: true, onChange: (value) => editable && onFormulaTypeChange(form, value) })))))),
                React.createElement("div", { className: styles.body },
                    referencesData?.hasReferences && React.createElement("p", { className: styles.referenced }, referencedWarning),
                    renderContent(values[CustomVariableFormFields.formula]?.type),
                    (!!unitsError || !!unitsWarningMessage) && (React.createElement("div", { className: styles.variableError },
                        unitsError ? React.createElement(WarningSquareIcon, null) : React.createElement(InfoSquareIcon, null),
                        React.createElement("span", { className: styles.text }, unitsError || unitsWarningMessage))),
                    React.createElement(Input, { component: TextInput, name: CustomVariableFormFields.note, label: "Note", textarea: true, readonly: !editable, autoHeightMin: 72, autoHeightMax: 184, classes: { container: styles.note, label: styles.textareaLabel, component: styles.textarea } }))))) }));
};
export default CustomVariableDialog;
